import { db } from "@/firebase/init";
import { doc, updateDoc, getDoc } from "firebase/firestore";

/**
 * Increment the video ideas generated count for a user.
 * Resets the count if 30 days have passed since the period start date.
 */
export const incrementVideoIdeasGenerated = async (userId) => {
  try {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      throw new Error("User document not found");
    }
    
    const userData = userSnap.data();
    const stats = userData.stats || {};
    const now = new Date();
    
    // Check if stats exist and if period start date exists
    let periodStartDate = stats.periodStartDate ? new Date(stats.periodStartDate) : null;
    let currentCount = stats.videoIdeasGenerated || 0;
    
    // If no period start date, this is the first generation
    if (!periodStartDate) {
      periodStartDate = now;
      currentCount = 0;
      console.log('Starting new 30-day period:', periodStartDate.toISOString());
    } else {
      // Calculate days since period start
      const daysSinceStart = Math.floor((now - periodStartDate) / (1000 * 60 * 60 * 24));
      
      // If 30 or more days have passed, reset the period
      if (daysSinceStart >= 30) {
        periodStartDate = now;
        currentCount = 0;
        console.log('30-day period expired. Starting new period:', periodStartDate.toISOString());
      }
    }
    
    // Increment the count
    const newCount = currentCount + 1;
    
    // Update Firestore
    await updateDoc(userRef, {
      "stats.videoIdeasGenerated": newCount,
      "stats.periodStartDate": periodStartDate.toISOString(),
      "stats.lastGenerationDate": now.toISOString()
    });
    
    console.log(`Video generation count updated: ${newCount} (Period started: ${periodStartDate.toLocaleDateString()})`);
    
    return {
      count: newCount,
      periodStartDate: periodStartDate.toISOString(),
      daysRemaining: 30 - Math.floor((now - periodStartDate) / (1000 * 60 * 60 * 24))
    };
  } catch (error) {
    console.error("Error updating video generation stats:", error);
    throw error;
  }
};

/**
 * Get current generation stats for a user
 */
export const getGenerationStats = async (userId) => {
  try {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      return { count: 0, daysRemaining: 30, periodStartDate: null };
    }
    
    const stats = userSnap.data().stats || {};
    const periodStartDate = stats.periodStartDate ? new Date(stats.periodStartDate) : null;
    const now = new Date();
    
    if (!periodStartDate) {
      return { count: 0, daysRemaining: 30, periodStartDate: null };
    }
    
    const daysSinceStart = Math.floor((now - periodStartDate) / (1000 * 60 * 60 * 24));
    const daysRemaining = Math.max(0, 30 - daysSinceStart);
    
    return {
      count: stats.videoIdeasGenerated || 0,
      daysRemaining,
      periodStartDate: periodStartDate.toISOString()
    };
  } catch (error) {
    console.error("Error getting generation stats:", error);
    throw error;
  }
};
